import { Box, Flex, FormControl, Text, VStack } from '@chakra-ui/react';
import type { UTCDate } from '@date-fns/utc';
import { Select, type SingleValue } from 'chakra-react-select';
import { clamp } from 'date-fns';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { SelectionListControls } from '../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import {
  type DateRangeOption,
  DateRangePresets,
} from '../../constants/datePresets';
import { useAvailableDateRangeWithTrialLag } from '../../hooks/useAvailableDateRangeWithTrialLag';
import { useDateRangeFilter, useFilterState } from '../../hooks/useFilters';
import { useViewDateGranularity } from '../../hooks/useViewDateGranularity';
import { type DateRangeFilter, FilterId } from '../../types/filters';
import { localToUTC, utcToLocal } from '../../utils/dateUtils';
import './DateRangeFilter.css';

import {
  computeDateRangeFromOption,
  identifyDateRangePreset,
  validateDateRange,
} from './utils';

interface DateRangeFilterFormProps {
  closeMenu: () => void;
  allowedPresetValues?: string[];
  /** Which date-range filter this form drives. Defaults to DATE_RANGE. */
  filterId?: FilterId;
  /**
   * Force the picker granularity instead of deriving it from the page's
   * metrics. Used by the posting Post Date filter to stay day-level ('week').
   */
  granularity?: 'week' | 'month';
}

export const DateRangeFilterForm: React.FC<DateRangeFilterFormProps> = ({
  closeMenu,
  allowedPresetValues,
  filterId = FilterId.DATE_RANGE,
  granularity,
}) => {
  const { setDateRange } = useDateRangeFilter(filterId);
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const { filterState: rawDateRangeFilter } =
    useFilterState<DateRangeFilter>(filterId);
  const pageGranularity = useViewDateGranularity();
  const viewGranularity = granularity ?? pageGranularity;

  const [selectedOption, setSelectedOption] = useState<DateRangeOption | null>(
    null
  );

  const [localStartDate, setLocalStartDate] = useState<UTCDate | null>(null);
  const [localEndDate, setLocalEndDate] = useState<UTCDate | null>(null);

  const [error, setError] = useState<string>('');

  const systemMinDate =
    viewGranularity === 'month'
      ? (availableDateRange?.month.min ?? null)
      : (availableDateRange?.week.min ?? null);
  const systemMaxDate =
    viewGranularity === 'month'
      ? (availableDateRange?.month.max ?? null)
      : (availableDateRange?.week.max ?? null);

  const isLoading = !availableDateRange;

  useEffect(() => {
    if (isLoading || !systemMinDate || !systemMaxDate) return;

    const displayStartDate = rawDateRangeFilter?.min
      ? (clamp(rawDateRangeFilter.min, {
          start: systemMinDate,
          end: systemMaxDate,
        }) as UTCDate)
      : null;
    const displayEndDate = rawDateRangeFilter?.max
      ? (clamp(rawDateRangeFilter.max, {
          start: systemMinDate,
          end: systemMaxDate,
        }) as UTCDate)
      : null;

    const presetKey = identifyDateRangePreset(
      rawDateRangeFilter,
      viewGranularity,
      availableDateRange
    );

    const foundOption =
      DateRangePresets.find((o: DateRangeOption) => o.value === presetKey) ||
      null;

    setSelectedOption(foundOption);
    setLocalStartDate(displayStartDate);
    setLocalEndDate(displayEndDate);
    setError('');
  }, [
    rawDateRangeFilter,
    isLoading,
    systemMinDate,
    systemMaxDate,
    viewGranularity,
    availableDateRange,
  ]);

  const updateLocalDates = useCallback(
    (option: DateRangeOption) => {
      setSelectedOption(option);
      setError('');

      if (isLoading || !systemMinDate || !systemMaxDate) {
        setLocalStartDate(null);
        setLocalEndDate(null);
        return;
      }

      if (option.value === 'max') {
        setLocalStartDate(null);
        setLocalEndDate(null);
      } else if (option.value === 'custom') {
        setLocalStartDate(rawDateRangeFilter?.min ?? localStartDate);
        setLocalEndDate(rawDateRangeFilter?.max ?? localEndDate);
      } else {
        const computed = computeDateRangeFromOption(
          option,
          availableDateRange,
          viewGranularity
        );
        if (computed) {
          let finalStartDate: UTCDate | null =
            computed.start < availableDateRange[viewGranularity].min
              ? availableDateRange[viewGranularity].min
              : computed.start;
          let finalEndDate: UTCDate | null =
            computed.end > availableDateRange[viewGranularity].max
              ? availableDateRange[viewGranularity].max
              : computed.end;

          if (finalStartDate && finalEndDate && finalStartDate > finalEndDate) {
            finalStartDate = availableDateRange[viewGranularity].min;
            finalEndDate = availableDateRange[viewGranularity].max;
          }
          setLocalStartDate(finalStartDate);
          setLocalEndDate(finalEndDate);
        } else {
          setLocalStartDate(availableDateRange[viewGranularity].min);
          setLocalEndDate(availableDateRange[viewGranularity].max);
        }
      }
    },
    [
      isLoading,
      systemMinDate,
      systemMaxDate,
      rawDateRangeFilter?.min,
      rawDateRangeFilter?.max,
      localStartDate,
      localEndDate,
      availableDateRange,
      viewGranularity,
    ]
  );

  const handleOptionChange = useCallback(
    (newOption: SingleValue<DateRangeOption>) => {
      if (!newOption) return;
      updateLocalDates(newOption);
    },
    [updateLocalDates]
  );

  const handleApply = () => {
    if (isLoading || (selectedOption?.value === 'custom' && error)) return;
    if (!selectedOption) return;

    // Save the raw local dates representing user intent
    setDateRange(
      localStartDate ? localToUTC(localStartDate) : null,
      localEndDate ? localToUTC(localEndDate) : null
    );
    closeMenu();
  };

  const handleClear = () => {
    setError('');
    setDateRange(null, null);
    closeMenu();
  };

  useEffect(() => {
    if (selectedOption?.value !== 'custom') return;
    const msg = validateDateRange(
      localStartDate,
      localEndDate,
      systemMinDate,
      systemMaxDate
    );
    setError(msg);
  }, [
    localStartDate,
    localEndDate,
    selectedOption,
    systemMinDate,
    systemMaxDate,
  ]);

  const isCustomSelected = selectedOption?.value === 'custom';

  const commonPickerProps = (() => {
    const base = {
      minDate: systemMinDate ?? undefined,
      maxDate: systemMaxDate ?? undefined,
      placeholderText: viewGranularity === 'week' ? 'YYYY-MM-DD' : 'YYYY-MM',
    };

    if (viewGranularity === 'month') {
      return {
        ...base,
        showMonthYearPicker: true,
        dateFormat: 'yyyy-MM',
        showMonthDropdown: false,
        showYearDropdown: true,
        dropdownMode: 'select',
        yearDropdownItemNumber: 15,
        scrollableYearDropdown: true,
      } as const;
    } else {
      return {
        ...base,
        showWeekPicker: true,
        showWeekNumbers: true,
        dateFormat: 'yyyy-MM-dd',
        showMonthDropdown: true,
        showYearDropdown: true,
        dropdownMode: 'select',
        yearDropdownItemNumber: 15,
        scrollableYearDropdown: true,
      } as const;
    }
  })();

  const renderDateInput = (
    label: string,
    selected: UTCDate | null,
    onChange: (date: UTCDate | null) => void,
    pickerMinDate: UTCDate | null,
    pickerMaxDate: UTCDate | null,
    isInvalid?: boolean,
    testId?: string
  ) => (
    <FormControl flex={1} isDisabled={isLoading}>
      <Text fontSize="sm" pb={1}>
        {label}
      </Text>
      <DatePicker
        selected={selected ? utcToLocal(selected) : null}
        onChange={(date) => onChange(date ? localToUTC(date) : null)}
        {...commonPickerProps}
        minDate={pickerMinDate ? utcToLocal(pickerMinDate) : null}
        maxDate={pickerMaxDate ? utcToLocal(pickerMaxDate) : null}
        onBlur={(event) => {
          event.stopPropagation();
        }}
        customInput={
          <Flex
            as="input"
            p={2}
            border="1px solid"
            borderColor={isInvalid ? 'red.500' : 'gray.200'}
            borderRadius="md"
            width="100%"
            fontSize="sm"
            data-testid={testId}
            autoComplete="off"
          />
        }
      />
    </FormControl>
  );

  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <Select<DateRangeOption, false>
          options={DateRangePresets.filter(
            (option) =>
              option.hidden !== true &&
              (!allowedPresetValues ||
                allowedPresetValues.includes(option.value))
          )}
          value={selectedOption}
          onChange={handleOptionChange}
          size="sm"
          isDisabled={isLoading}
          id="filter-range"
        />
      </FormControl>

      {isCustomSelected && (
        <VStack align="stretch" spacing={1} mb={2}>
          <Box>
            <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
              {renderDateInput(
                'Start Date',
                localStartDate,
                setLocalStartDate,
                systemMinDate ?? null,
                localEndDate || systemMaxDate || null,
                !!(
                  error &&
                  localStartDate &&
                  systemMinDate &&
                  localStartDate < systemMinDate
                ),
                'custom-range-start-date'
              )}
              {renderDateInput(
                'End Date',
                localEndDate,
                setLocalEndDate,
                localStartDate || systemMinDate || null,
                systemMaxDate ?? null,
                !!(
                  error &&
                  localEndDate &&
                  systemMaxDate &&
                  localEndDate > systemMaxDate
                ),
                'custom-range-end-date'
              )}
            </Flex>
          </Box>
        </VStack>
      )}

      {error && (
        <Text fontSize="sm" color="red.500">
          {error}
        </Text>
      )}

      <SelectionListControls
        onClear={handleClear}
        onSubmit={handleApply}
        onClose={closeMenu}
        clearText="Clear"
        submitText="Apply"
        isSubmitDisabled={!!error || isLoading}
      />
    </VStack>
  );
};
